import React, { useEffect, useRef, useState } from 'react';
import { Empty, Skeleton, Spin, Tooltip } from 'antd';
import { LockOutlined, MessageOutlined, PhoneOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import ConciergeBellIcon from '../../receptionist_ai/components/ConciergeBellIcon';
import { ContactFollowedBy, ContactTagList } from '../../contacts/components/ContactTags';
import { ConversationDisplay, formatActivityPreview, formatConversationTime } from '../types/ConversationDisplay';

interface ConversationListProps {
  conversations: ConversationDisplay[];
  selectedKey: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onSelect: (conversation: ConversationDisplay) => void;
  onLoadMore: () => void;
}

// Distance to the bottom of the list from which the next rows are rendered, then the next page asked
const CONVERSATIONS_LOAD_THRESHOLD = 400;
// Rows rendered per step, like the contacts list: a supervisor loads every line at once
const CONVERSATIONS_RENDER_STEP = 100;

const ActivityIcon: React.FC<{ conversation: ConversationDisplay }> = ({ conversation }) => {
  const { type, missed } = conversation.lastActivity;
  if (type === 'receptionist') return <ConciergeBellIcon />;
  if (type === 'call') return <PhoneOutlined className={classNames({ 'conversations__preview-icon--missed': missed })} />;
  return <MessageOutlined />;
};

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedKey,
  isLoading,
  isLoadingMore,
  hasMore,
  onSelect,
  onLoadMore,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(CONVERSATIONS_RENDER_STEP);
  const hasHiddenRows = visibleCount < conversations.length;

  const handleScroll = () => {
    const list = listRef.current;
    if (!list || list.scrollHeight - list.scrollTop - list.clientHeight >= CONVERSATIONS_LOAD_THRESHOLD) return;
    if (hasHiddenRows) setVisibleCount((count) => count + CONVERSATIONS_RENDER_STEP);
    else if (hasMore) onLoadMore();
  };

  // Filters can leave too few rows to scroll: the next pages are asked until the list fills its height
  useEffect(() => {
    const list = listRef.current;
    if (!list || hasHiddenRows || !hasMore || isLoadingMore) return;
    if (list.scrollHeight - list.clientHeight < CONVERSATIONS_LOAD_THRESHOLD) onLoadMore();
  }, [conversations, hasHiddenRows, hasMore, isLoadingMore, onLoadMore]);

  if (isLoading) {
    return (
      <div className="conversations__list-loading">
        <Skeleton active avatar paragraph={{ rows: 2 }} />
        <Skeleton active avatar paragraph={{ rows: 2 }} />
        <Skeleton active avatar paragraph={{ rows: 2 }} />
      </div>
    );
  }

  if (conversations.length === 0 && !hasMore) {
    return <Empty className="conversations__empty" description="Aucune conversation" />;
  }

  return (
    <div className="conversations__list" ref={listRef} onScroll={handleScroll}>
      {conversations.slice(0, visibleCount).map((conversation) => (
        <button
          key={conversation.key}
          type="button"
          className={classNames('conversations__item', {
            'conversations__item--active': conversation.key === selectedKey,
            'conversations__item--unread': conversation.unreadCount > 0,
          })}
          onClick={() => onSelect(conversation)}
        >
          <span className="contacts__avatar">{conversation.initials}</span>
          <span className="conversations__item-body">
            <span className="conversations__item-head">
              <span className="conversations__item-name">{conversation.fullName}</span>
              {conversation.contact?.isConfidential && (
                <Tooltip title="Confidentiel - non enregistré">
                  <LockOutlined className="contacts__name-confidential" aria-label="Confidentiel" />
                </Tooltip>
              )}
              {conversation.unreadCount > 0 && <span className="conversations__badge">{conversation.unreadCount}</span>}
              <span className="conversations__item-time">{formatConversationTime(conversation.lastActivity.timestamp)}</span>
            </span>
            <span className="conversations__item-preview">
              <ActivityIcon conversation={conversation} />
              <span className="conversations__item-preview-text">{formatActivityPreview(conversation.lastActivity)}</span>
            </span>
            <span className="conversations__item-meta">
              <ContactFollowedBy label={conversation.followedBy} isOwn={conversation.isOwn} />
              {conversation.typology && <ContactTagList tags={[conversation.typology]} />}
            </span>
          </span>
        </button>
      ))}

      {isLoadingMore && (
        <div className="conversations__list-more">
          <Spin size="small" /> Chargement des conversations suivantes…
        </div>
      )}
    </div>
  );
};

export default ConversationList;
